import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  Users,
  Package,
  Calendar,
  Shield,
  CheckCircle,
  Clock,
  DollarSign,
  FileText,
  Settings,
  Bell,
  Database,
  Smartphone as MobileIcon,
  Monitor
} from 'lucide-react';
import FeatureDetail from '../components/FeatureDetail';
import { PLAY_STORE_URL, WEB_APP_URL } from '../config/urls';

const Features = () => {
  const mainFeatures = [
    {
      id: 'customer-management',
      icon: <Users className="feature-icon" />,
      title: 'Comprehensive Customer Management',
      description: 'Maintain detailed customer profiles and track their preferences for personalized service.',
      details: [
        'Complete customer database',
        'Service history tracking',
        'Loyalty program management',
        'Customer feedback collection',
        'Package usage monitoring'
      ],
      image: '/Customer Management.jpg',
      color: 'from-green-500 to-emerald-500'
    },
    {
      id: 'package-management',
      icon: <Package className="feature-icon" />,
      title: 'Package Management',
      description: 'Create and manage spa packages with flexible pricing, validity periods, and usage tracking.',
      details: [
        'Create custom spa packages',
        'Set package validity periods',
        'Track package usage',
        'Package renewal management',
        'Customer package history'
      ],
      image: '/Package Management.jpg',
      color: 'from-purple-500 to-pink-500'
    },
    {
      id: 'analytics-dashboard',
      icon: <BarChart3 className="feature-icon" />,
      title: 'Real-Time Analytics Dashboard',
      description: 'Get instant insights into your business performance with comprehensive reports and visualizations.',
      details: [
        'Revenue tracking and trends',
        'Customer analytics',
        'Therapist performance metrics',
        'Popular services analysis',
        'Financial reporting'
      ],
      image: '/Real-time analytics.jpg',
      color: 'from-orange-500 to-red-500'
    },
    {
      id: 'booking-management',
      icon: <Calendar className="feature-icon" />,
      title: 'Smart Booking Management',
      description: 'Streamline your appointment scheduling with intelligent room allocation and therapist assignment.',
      details: [
        'Real-time room availability tracking',
        'Customer preference management',
        'Booking conflict prevention',
        'Mobile booking notifications',
        'Appointment reminders'
      ],
      image: '/Booking Management.jpg',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'mobile-app',
      icon: <MobileIcon className="feature-icon" />,
      title: 'Mobile & Web Access',
      description: 'Use the native Android app on the go or the web app from any browser. Same data, same features, one account.',
      details: [
        'Native Android application',
        'Full-featured web dashboard',
        'Real-time sync across devices',
        'Offline support on mobile',
        'Touch-optimized and desktop-friendly'
      ],
      image: '/Mobile design first.jpg',
      color: 'from-indigo-500 to-blue-500'
    },
    {
      id: 'security-reliability',
      icon: <Shield className="feature-icon" />,
      title: 'Enterprise-Grade Security',
      description: 'Your data is protected with industry-standard security measures and reliable cloud infrastructure.',
      details: [
        'Data encryption',
        'Regular backups',
        '99.9% uptime guarantee',
        'GDPR compliance',
        'Secure data transmission'
      ],
      image: '/Enterprise-Grade Security.png',
      color: 'from-teal-500 to-green-500'
    }
  ];

  const additionalFeatures = [
    {
      icon: <Clock size={24} />,
      title: 'Flexible Scheduling',
      description: 'Customize therapy durations from 30 minutes to 2 hours with flexible time slots.'
    },
    {
      icon: <DollarSign size={24} />,
      title: 'Package Management',
      description: 'Create and manage customer packages with usage tracking and expiration dates.'
    },
    {
      icon: <FileText size={24} />,
      title: 'Expense Tracking',
      description: 'Monitor business expenses with detailed categorization and reporting.'
    },
    {
      icon: <Settings size={24} />,
      title: 'Staff Management',
      description: 'Manage therapist profiles, salaries, and performance tracking.'
    },
    {
      icon: <Bell size={24} />,
      title: 'Smart Notifications',
      description: 'Get real-time alerts for bookings, payments, and important updates.'
    },
    {
      icon: <Database size={24} />,
      title: 'Data Synchronization',
      description: 'Real-time data sync across all devices with offline support.'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-slate-50 to-slate-200">
        <div className="container">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-large text-gradient mb-4">
              Powerful Features for Modern Spa Management
            </h1>
            <p className="text-small text-gray-600 max-w-3xl mx-auto mb-8">
              All features are available on both mobile and web. Discover how Wellness Pro can transform your spa operations.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a 
                href={WEB_APP_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-3 rounded-xl hover:from-emerald-500 hover:to-teal-500 shadow-lg shadow-emerald-500/25 transition-all duration-300"
              >
                <Monitor className="w-5 h-5" />
                Launch Web App
              </a>
              <a 
                href={PLAY_STORE_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-cyan-600 hover:via-blue-600 hover:to-indigo-700 transition-all duration-300 hover:scale-105 shadow-lg shadow-cyan-500/40"
              >
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <div className="flex flex-col items-start">
                  <span className="text-[10px] leading-tight">GET IT ON</span>
                  <span className="text-lg font-bold leading-tight">Google Play</span>
                </div>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Features */}
      <section className="section-padding bg-white">
        <div className="container">
          {mainFeatures.map((feature, index) => (
            <motion.div
              key={feature.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <FeatureDetail 
                {...feature} 
                reverse={index % 2 === 1}
              />
            </motion.div>
          ))}
        </div>
      </section>

      {/* Additional Features */}
      <section className="section-padding bg-gradient-primary relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `
                radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)
              `,
              backgroundSize: '400px 400px'
            }}
          ></div>
        </div>
        <div className="container relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-large text-white mb-4">
              And Much More...
            </h2>
            <p className="text-small text-white/90 max-w-2xl mx-auto">
              Wellness Pro includes dozens of additional features designed to make your spa management effortless and efficient.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {additionalFeatures.map((feature, index) => (
              <motion.div
                key={index}
                className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 text-center text-white transition-all duration-300 hover:bg-white/15 hover:-translate-y-1"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6 text-white">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-4 text-white">{feature.title}</h3>
                <p className="text-white/90 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-white">
        <div className="container">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-large text-gradient mb-6">
              Ready to Experience All These Features?
            </h2>
            <p className="text-small text-gray-600 mb-8 max-w-2xl mx-auto">
              Start your free trial today and discover how Wellness Pro can transform your spa business operations.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-2xl mx-auto mb-8">
              <div className="flex items-center gap-3 text-gray-700 font-medium">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span>30-day free trial with full access</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700 font-medium">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span>No setup fees or hidden costs</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700 font-medium">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span>24/7 customer support</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 justify-center">
              <a 
                href={WEB_APP_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-3 rounded-xl hover:from-emerald-500 hover:to-teal-500 shadow-lg shadow-emerald-500/25 transition-all duration-300"
              >
                <Monitor className="w-5 h-5" />
                Launch Web App
              </a>
              <a 
                href={PLAY_STORE_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-cyan-600 hover:via-blue-600 hover:to-indigo-700 transition-all duration-300 hover:scale-105 shadow-lg shadow-cyan-500/40"
              >
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <div className="flex flex-col items-start">
                  <span className="text-[10px] leading-tight">GET IT ON</span>
                  <span className="text-lg font-bold leading-tight">Google Play</span>
                </div>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Features;
